import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { toUiDifficulty } from '../api/difficulty';
import type { ScenarioDetail, ScenarioSummary } from '../api/types';

/**
 * Scenario query hooks — backed by `services/catalog/handler.py`.
 */

export const scenarioKeys = {
  all: ['scenarios'] as const,
  list: (filter?: string) => [...scenarioKeys.all, 'list', filter ?? ''] as const,
  detail: (slug: string) => [...scenarioKeys.all, 'detail', slug] as const,
};

export function useScenarios(filter?: string) {
  return useQuery<ReadonlyArray<ScenarioSummary>>({
    queryKey: scenarioKeys.list(filter),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/scenarios', { method: 'GET', signal });
      const raw = Array.isArray(resp)
        ? (resp as ReadonlyArray<ScenarioSummary>)
        : (resp as { items?: ReadonlyArray<ScenarioSummary> })?.items ?? [];
      // Normalize here, not at each render site: the API sends easy/medium/hard
      // and every DIFFICULTY_VARIANT lookup in the UI is keyed on the UI words.
      const items = raw.map((s) => ({ ...s, difficulty: toUiDifficulty(s.difficulty) }));
      const normalized = filter?.trim().toLowerCase();
      if (!normalized) return items;
      return items.filter(
        (s) =>
          s.name.toLowerCase().includes(normalized) ||
          s.slug.includes(normalized) ||
          s.domain.includes(normalized) ||
          s.difficulty.includes(normalized),
      );
    },
  });
}

export function useScenario(slug: string | undefined) {
  return useQuery<ScenarioDetail>({
    queryKey: scenarioKeys.detail(slug ?? ''),
    enabled: Boolean(slug),
    queryFn: async ({ signal }) =>
      apiFetch<ScenarioDetail>('/scenarios/{slug}', {
        method: 'GET',
        pathParams: { slug: slug ?? '' },
        signal,
      }).then((d) => ({ ...d, difficulty: toUiDifficulty(d.difficulty) })),
  });
}

export interface DeployScenarioInput {
  scenarioSlug: string;
  durationMins?: number;
  variant?: string;
  /** Team mode (operator decision 2026-05-25): the Cognito sub for each
   *  trainee assigned to this range. Backend sets workstation count to
   *  trainee_ids.length and tags each replica with forge:assigned_to=<id>. */
  traineeIds?: ReadonlyArray<string>;
  cohortId?: string;
  name?: string;
}

export interface DeployScenarioResponse {
  rangeId: string;
  status: 'pending' | 'provisioning';
  traineeIds?: ReadonlyArray<string>;
  cohortId?: string | null;
  numTrainees?: number;
}

export function useDeployScenario() {
  const qc = useQueryClient();
  return useMutation<DeployScenarioResponse, Error, DeployScenarioInput>({
    mutationFn: async (input) =>
      apiFetch<DeployScenarioResponse>('/ranges', {
        method: 'POST',
        body: {
          template_slug: input.scenarioSlug,
          deploy_mode: 'full',
          ...(input.durationMins !== undefined ? { duration_mins: input.durationMins } : {}),
          ...(input.variant !== undefined ? { variant: input.variant } : {}),
          ...(input.traineeIds && input.traineeIds.length > 0
            ? { trainee_ids: input.traineeIds }
            : {}),
          ...(input.cohortId ? { cohort_id: input.cohortId } : {}),
          ...(input.name ? { name: input.name } : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ranges'] });
    },
    // DeployRangeModal renders the failure inline next to the form it came
    // from — a toast on top of it would just duplicate the message.
    meta: { silentError: true },
  });
}
